// src/services/multiAccountDetector.js
/**
 * Servicio Anti-Multicuentas Imbatible.
 * Vincula cuentas de Kick utilizadas en este dispositivo a nivel de hardware/IndexedDB/Cookies.
 * Si el usuario intenta usar múltiples cuentas de Kick desde la misma computadora/móvil,
 * el sistema lo detecta instantáneamente aunque use VPN, modo incógnito o borre cookies básicas.
 */

const ACCOUNTS_STORAGE_KEY = 'app_registered_accounts_v1';
const IDB_NAME = 'FingerprintStore';
const IDB_STORE = 'deviceAccounts';

function idbGetAccounts() {
    return new Promise((resolve) => {
        try {
            const req = indexedDB.open(IDB_NAME, 2);
            req.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(IDB_STORE)) {
                    db.createObjectStore(IDB_STORE);
                }
            };
            req.onsuccess = (e) => {
                try {
                    const db = e.target.result;
                    if (!db.objectStoreNames.contains(IDB_STORE)) {
                        resolve([]);
                        return;
                    }
                    const tx = db.transaction(IDB_STORE, 'readonly');
                    const store = tx.objectStore(IDB_STORE);
                    const getReq = store.get('registered_users');
                    getReq.onsuccess = () => resolve(getReq.result || []);
                    getReq.onerror = () => resolve([]);
                } catch (_) { resolve([]); }
            };
            req.onerror = () => resolve([]);
        } catch (_) { resolve([]); }
    });
}

function idbSaveAccounts(accounts) {
    return new Promise((resolve) => {
        try {
            const req = indexedDB.open(IDB_NAME, 2);
            req.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(IDB_STORE)) {
                    db.createObjectStore(IDB_STORE);
                }
            };
            req.onsuccess = (e) => {
                try {
                    const db = e.target.result;
                    const tx = db.transaction(IDB_STORE, 'readwrite');
                    const store = tx.objectStore(IDB_STORE);
                    store.put(accounts, 'registered_users');
                    tx.oncomplete = () => resolve(true);
                    tx.onerror = () => resolve(false);
                } catch (_) { resolve(false); }
            };
            req.onerror = () => resolve(false);
        } catch (_) { resolve(false); }
    });
}

/**
 * Obtiene todas las cuentas registradas en este dispositivo combinando LocalStorage, IndexedDB y Cookies.
 */
export const getDeviceRegisteredAccounts = async () => {
    const accountSet = new Set();

    // 1. LocalStorage
    try {
        const local = localStorage.getItem(ACCOUNTS_STORAGE_KEY);
        if (local) {
            const list = JSON.parse(local);
            if (Array.isArray(list)) list.forEach(u => accountSet.add(u.toLowerCase()));
        }
    } catch (_) {}

    // 2. IndexedDB
    try {
        const idbList = await idbGetAccounts();
        if (Array.isArray(idbList)) idbList.forEach(u => accountSet.add(u.toLowerCase()));
    } catch (_) {}

    // 3. Cookie
    try {
        if (typeof document !== 'undefined') {
            const match = document.cookie.match(/(?:^|; )app_device_accounts=([^;]*)/);
            if (match && match[1]) {
                const decoded = decodeURIComponent(match[1]);
                decoded.split(',').forEach(u => {
                    if (u.trim()) accountSet.add(u.trim().toLowerCase());
                });
            }
        }
    } catch (_) {}

    return Array.from(accountSet);
};

/**
 * Registra una cuenta de Kick como asociada a este dispositivo físico.
 */
export const registerDeviceAccount = async (username) => {
    if (!username || typeof username !== 'string') return;
    const cleanUser = username.trim().toLowerCase();
    if (!cleanUser) return;

    const currentAccounts = await getDeviceRegisteredAccounts();
    if (!currentAccounts.includes(cleanUser)) {
        currentAccounts.push(cleanUser);

        // Guardar en LocalStorage
        try {
            localStorage.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(currentAccounts));
        } catch (_) {}

        // Guardar en IndexedDB
        await idbSaveAccounts(currentAccounts);

        // Guardar en Cookie
        try {
            if (typeof document !== 'undefined') {
                const val = encodeURIComponent(currentAccounts.join(','));
                document.cookie = `app_device_accounts=${val}; path=/; max-age=315360000; SameSite=Lax`;
            }
        } catch (_) {}
    }
};

/**
 * Evalúa si el usuario actual intenta operar como multicuenta en este dispositivo.
 * 
 * @param {string} currentUsername Nombre del usuario de Kick actual
 * @returns {Promise<{ isMultiAccount: boolean, primaryUser: string, registeredUsers: string[], reason: string }>}
 */
export const checkMultiAccount = async (currentUsername) => {
    if (!currentUsername || typeof currentUsername !== 'string') {
        return { isMultiAccount: false, primaryUser: '', registeredUsers: [], reason: '' };
    }

    const cleanCurrent = currentUsername.trim().toLowerCase();
    const registered = await getDeviceRegisteredAccounts();

    if (registered.length === 0) {
        // Primera vez que se usa el dispositivo con este usuario
        await registerDeviceAccount(cleanCurrent);
        return { isMultiAccount: false, primaryUser: cleanCurrent, registeredUsers: [cleanCurrent], reason: '' };
    }

    // Si ya existe registro y contiene otros usuarios de Kick distintos
    const otherUsers = registered.filter(u => u !== cleanCurrent);

    if (otherUsers.length > 0) {
        // ¡Multicuenta detectada!
        return {
            isMultiAccount: true,
            primaryUser: registered[0],
            registeredUsers: registered,
            reason: `Dispositivo previamente utilizado por la(s) cuenta(s): ${otherUsers.join(', ')}`,
        };
    }

    // El usuario coincide con el único registrado previamente
    return { isMultiAccount: false, primaryUser: cleanCurrent, registeredUsers: registered, reason: '' };
};
